import { RadioGroup } from '@perch-labs/shrike';
import { useEffect, useState } from 'react';
import { Button } from '~/components/button';
import { CountySelectComponent } from '~/components/dropdowns/county/county-select.component';
import { HouseholdOccupancySelectComponent } from '~/components/dropdowns/house-hold-occupancy/household-occupancy-select.component';
import { LmiQualifyingProgramSelectComponent } from '~/components/dropdowns/lmi-qualifying-program/lmi-qualifying-program-select.component';
import { Form } from '~/components/form';
import { Inputs } from '~/components/inputs';
import { Container } from '~/components/layout';
import { ReviewBlock } from '~/components/review-block';
import { Text } from '~/components/typography';
import { useForm } from '~/hooks/use-form';
import { LmiStatus, ReviewContext } from '~/machines/review-form';
import { ReviewFormCopy } from '~/screens/review-form';
import { VnmTermsContext } from '~/screens/vnm-terms';
import { CountyIncomeLimits, njDefaultCounties } from './county-income-limits';

interface Props {
  context: VnmTermsContext | ReviewContext;
  flags?: {
    showCountySelect?: boolean;
    showHouseholdOccupancy?: boolean;
    showLmiProgram?: boolean;
    includeHouseIncome?: boolean;
    showLmiOptions?: boolean;
    requiresAddressFields?: boolean;
  };
  copy: ReviewFormCopy;
  next: (data) => void;
  loading: boolean;
}

export const SelfAttestationForm = ({ context, flags, copy, next, loading }: Props) => {
  const { zipCode, id: userId } = context.funnelPacket.user;

  const lmiOptions: LmiStatus[] = ['isLmi', 'maybeLmi', 'notLmi'];
  const [nextButtonText, setNextButtonText] = useState(copy.next);
  const state = context.funnelPacket?.utility?.state;

  const initialFormState = {
    ...(flags?.showLmiOptions && { lmiStatus: 'isLmi' }),
    ...(flags?.showCountySelect && { county: '' }),
    ...(flags?.showHouseholdOccupancy && { houseOccupancy: '' }),
    ...(flags?.showLmiProgram && { lmiProgram: '' }),
    ...(flags?.requiresAddressFields && { address: '' }),
  };

  const [form, updateForm, isValid] = useForm(initialFormState);

  useEffect(() => {
    switch (form.lmiStatus) {
      case 'isLmi':
        updateForm('isLmi', { isValid: true, name: 'lmiStatus' });
        setNextButtonText(copy.next);
        break;
      case 'maybeLmi':
        setNextButtonText(copy.next);
        break;
      case 'notLmi':
        setNextButtonText('Continue');
        break;
      default:
        setNextButtonText(copy.next);
    }
  }, [form.lmiStatus]);

  const isLmiStatusValid = form.lmiStatus !== 'notLmi';

  const handleLmiStatusChange = status => updateForm(status, { name: 'lmiStatus', isValid: true });

  const shouldDisableButton = isLmiStatusValid ? !isValid : false;

  return (
    <Container addInputMargin>
      <Form
        onSubmit={() => {
          next({
            SelfAttestationData: {
              county: form.county,
              houseOccupancy: form.houseOccupancy,
              lmiProgram: form.lmiProgram,
              houseIncome: flags?.includeHouseIncome
                ? form.houseOccupancy &&
                  CountyIncomeLimits.getIncomeLimits(
                    (form.county ?? '').toLowerCase().split('county')[0].trim(),
                    Number(form.houseOccupancy) - 1,
                  )
                : '',
            },
            lmiStatus: flags?.showLmiOptions ? form.lmiStatus : undefined,
            address: flags?.requiresAddressFields ? form.address : undefined,
          });
        }}
      >
        {!!flags?.showLmiOptions && (
          <ReviewBlock>
            <ReviewBlock.Item label={copy.lmiSectionTitle} notice={copy.lmiSectionDescription} />
            <RadioGroup
              margin="24px 0 0 0"
              name="lmi-selection"
              options={(lmiOptions as LmiStatus[]).map(choice => ({
                id: choice,
                children: (
                  <Container
                    key={choice}
                    onClick={() => updateForm(choice, { name: 'lmiStatus', isValid: true })}
                  >
                    {copy[choice].primary}
                    <Text fontSize="small" margin="10px 0">
                      {copy[choice].secondary}
                    </Text>
                  </Container>
                ),
                value: choice,
              }))}
              isChecked={(_, value) => value === form.lmiStatus}
              onChange={handleLmiStatusChange}
              width="initial"
            />
          </ReviewBlock>
        )}
        {isLmiStatusValid ? (
          <>
            {flags?.requiresAddressFields ? (
              <Inputs.Address mb={5} state={state} onChange={updateForm} required />
            ) : null}
            {flags?.showCountySelect ? (
              <CountySelectComponent
                zipCode={zipCode!}
                defaultCounties={njDefaultCounties}
                userId={userId}
                onChange={value => updateForm(value, { name: 'county', isValid: true })}
                value={form.county}
              />
            ) : null}
            {flags?.showHouseholdOccupancy ? (
              <HouseholdOccupancySelectComponent
                onChange={value => updateForm(value, { name: 'houseOccupancy', isValid: true })}
                value={form.houseOccupancy ?? ''}
              />
            ) : null}
            {flags?.showLmiProgram ? (
              <LmiQualifyingProgramSelectComponent
                onChange={updateForm}
                value={form.lmiProgram ?? ''}
                optional
              />
            ) : null}
          </>
        ) : null}
        <Button
          mb={4}
          mode="primary"
          testId="review-form-button"
          type="submit"
          disabled={shouldDisableButton}
          loading={loading}
        >
          {nextButtonText}
        </Button>
      </Form>
    </Container>
  );
};
